import React, { useRef, useMemo } from 'react'
import cn from 'clsx'
import styled from 'styled-components'
import { rem } from 'polished'

import MarkdownRender from 'components/Luxkit/MarkdownRender'

import BodyText from 'components/Luxkit/Typography/BodyText'
import pluralize from 'lib/pluralize'
import { getInclusionIcon } from 'lib/getInclusionIcon'
import { isNonEmptyArray } from 'lib/arrayUtils'
import OfferTileInclusionsModal from './OfferTileInclusionsModal'
import getInclusionMinStayText from 'lib/getInclusionMinStayText'

const Root = styled.div`
  position: relative;
  margin-top: ${rem(16)};
  overflow: hidden;
`

const MoreInclusions = styled.div`
margin-top: ${rem(4)};
`

const StyledInclusionItemDiv = styled.div`
  display: flex;
`

const InclusionItemText = styled(BodyText)`
  margin-top: 0;
  padding-left: ${rem(8)};
  text-indent: ${rem(-2)};
`

const StyledIconDiv = styled.div`
  position: relative;
  top: 2px;
`

interface Props {
  className?: string;
  content?: string;
  tileInclusionsList?: App.TileInclusion[];
  heading: string;
  abTestOfferTile?: boolean;
  maxInclusionsCount?: number;
  offerURL?: string
  offerTitle: string
}

function OfferTileInclusions({
  className,
  content,
  tileInclusionsList,
  heading = 'Your handpicked inclusions',
  abTestOfferTile,
  maxInclusionsCount,
  offerURL,
  offerTitle,
}: Props) {
  const showReducedInclusions = abTestOfferTile || !!maxInclusionsCount

  const inclusionsEl = useRef(null)
  const shownInclusions = tileInclusionsList?.slice(0, maxInclusionsCount ?? 3)
  const numInclusionsLeft = isNonEmptyArray(tileInclusionsList) ? tileInclusionsList?.length - (maxInclusionsCount ?? 3) : 0

  const inclusionsFromContent = useMemo(() => {
    if (!content || !maxInclusionsCount) return null
    const inclusionsFromContent = content.split('\n').map(item => item.trim()).filter(item => item !== '')
    const visibleItems = inclusionsFromContent.slice(0, maxInclusionsCount)
    const remainingItemsCount = Math.max(inclusionsFromContent.length - maxInclusionsCount, 0)

    return (
      <div>
        {visibleItems.map((item, index) => (
          <MarkdownRender key={index} content={item} fontSize="medium" type="compact" />
        ))}
        {remainingItemsCount > 0 && <MoreInclusions>
          <BodyText variant="medium" weight="bold" colour="primary" underline>{`+ ${remainingItemsCount} more ${pluralize({ singular: 'inclusion', count: remainingItemsCount }).text}`}</BodyText>
        </MoreInclusions>}
      </div>
    )
  }, [content, maxInclusionsCount])

  return (
    <Root className={cn(className, { 'landing-page-test': abTestOfferTile })}>
      <BodyText variant="medium" weight="bold">{heading}</BodyText>
      <div ref={inclusionsEl}>
        {content && !tileInclusionsList?.length && maxInclusionsCount && inclusionsFromContent}
        {content && !tileInclusionsList?.length && !maxInclusionsCount && <MarkdownRender fontSize="medium" type="compact" content={content} />}
        {isNonEmptyArray(tileInclusionsList) &&
         (showReducedInclusions ? shownInclusions : tileInclusionsList)?.map((inclusion) => {
           const InclusionIcon = getInclusionIcon(inclusion.icon ?? '')
           const inclusionMinStayText = getInclusionMinStayText(inclusion)
           return (
             <StyledInclusionItemDiv key={inclusion.text}>
               <StyledIconDiv>
                 <InclusionIcon data-testid={inclusion.icon || 'default_icon'} size="XS" />
               </StyledIconDiv>
               <InclusionItemText
                  variant="medium"
                  weight={inclusion.isHighlighted ? 'bold' : 'normal'}
                >
                 {inclusion.text} {inclusionMinStayText}
               </InclusionItemText>
             </StyledInclusionItemDiv>
           )
         })}
      </div>
      {isNonEmptyArray(tileInclusionsList) && showReducedInclusions && numInclusionsLeft > 0 &&
        <MoreInclusions>
          <OfferTileInclusionsModal
            offerURL={offerURL}
            title={offerTitle}
            numInclusionsLeft={numInclusionsLeft}
            inclusionList={tileInclusionsList}
          />
        </MoreInclusions>
      }
    </Root>
  )
}

export default OfferTileInclusions
